import type { CompanyInfo } from "@elektr/shared";
import { useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Field, Input } from "@/components/ui/form";
import { ApiError } from "@/lib/api";
import { useT } from "@/lib/i18n";

export interface CompanyValues {
  name: string;
  phone: string;
  address: string;
}

/** Firma nomi, telefoni, manzili va logo. Logo faqat firma saqlangandan keyin yuklanadi. */
export function CompanyForm({
  company,
  onSave,
  onUpload,
  onRemoveLogo,
  saveLabel,
}: {
  company: CompanyInfo | null | undefined;
  onSave: (v: CompanyValues) => Promise<unknown>;
  onUpload: (file: File) => Promise<unknown>;
  onRemoveLogo?: () => Promise<unknown>;
  saveLabel: string;
}) {
  const { t } = useT();
  const [v, setV] = useState<CompanyValues>({
    name: company?.name ?? "",
    phone: company?.phone ?? "",
    address: company?.address ?? "",
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const file = useRef<HTMLInputElement>(null);

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      const body =
        e instanceof ApiError ? (e.body as Record<string, unknown> | undefined) : undefined;
      const first =
        body &&
        Object.values(body)
          .flat()
          .find((x) => typeof x === "string");
      setError(typeof first === "string" ? first : t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (v.name.trim())
          void run(() =>
            onSave({ name: v.name.trim(), phone: v.phone.trim(), address: v.address.trim() }),
          );
      }}
    >
      <Field label={t("settings.company_name")}>
        {(id) => (
          <Input
            id={id}
            value={v.name}
            onChange={(e) => setV({ ...v, name: e.target.value })}
            required
            maxLength={200}
          />
        )}
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t("settings.company_phone")}>
          {(id) => (
            <Input
              id={id}
              value={v.phone}
              onChange={(e) => setV({ ...v, phone: e.target.value })}
              maxLength={20}
            />
          )}
        </Field>
        <Field label={t("settings.company_address")}>
          {(id) => (
            <Input
              id={id}
              value={v.address}
              onChange={(e) => setV({ ...v, address: e.target.value })}
              maxLength={300}
            />
          )}
        </Field>
      </div>
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" disabled={busy || !v.name.trim()}>
          {saveLabel}
        </Button>
        <span className="text-sm text-muted-foreground">
          {company?.has_logo ? t("settings.logo_set") : t("settings.logo_none")}
        </span>
        <input
          ref={file}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="sr-only"
          data-testid="logo-file"
          aria-label={t("settings.logo_upload")}
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (f) void run(() => onUpload(f));
          }}
        />
        <Button
          type="button"
          variant="outline"
          disabled={busy || !company}
          onClick={() => file.current?.click()}
        >
          {t("settings.logo_upload")}
        </Button>
        {onRemoveLogo && company?.has_logo && (
          <Button
            type="button"
            variant="ghost"
            disabled={busy}
            onClick={() => void run(onRemoveLogo)}
          >
            {t("settings.logo_remove")}
          </Button>
        )}
      </div>
    </form>
  );
}
